import { getDevicesByBridge } from '@/api/device';
import BridgeEventBus from '@/utils/BridgeEventBus';

/**
 * 视频监控URL管理器
 * 用于动态获取视频监控URL
 */
const VideoUrlManager = {
  /**
   * 默认视频监控URL（空地址）
   */
  DEFAULT_URL: "http://",
  
  /**
   * 初始化管理器
   * 注册事件监听器
   */
  init() {
    console.log('VideoUrlManager初始化中...');
    
    // 使用全局BridgeEventBus（保留监听器，但简化处理逻辑）
    BridgeEventBus.$on('bridge-changed', this.handleBridgeChanged.bind(this));
    console.log('VideoUrlManager已初始化，使用动态获取模式');
    
    return this;
  },
  
  /**
   * 处理桥梁变更事件（已简化，因为改为动态获取）
   * @param {Object} bridge 桥梁对象
   */
  async handleBridgeChanged(bridge) {
    if (!bridge || !bridge.id) return;
    
    console.log(`VideoUrlManager检测到桥梁变更: ${bridge.name}(ID: ${bridge.id}) - 现在使用动态获取，无需预加载`);
    // 注意：由于改为动态获取视频URL，此方法现在主要用于日志记录
    // 实际的URL获取将在用户点击"视频监控"时进行
  },
  
  
  /**
   * 获取当前选中的桥梁ID
   * @returns {number|null} 桥梁ID，如果不存在则返回null
   */
  getCurrentBridgeId() {
    try {
      const bridgeData = localStorage.getItem('selectedBridge');
      if (bridgeData) {
        const bridge = JSON.parse(bridgeData);
        return bridge.id || null;
      }
    } catch (error) {
      console.error('获取当前桥梁ID失败:', error);
    }
    return null;
  },
  
  /**
   * 获取当前选中的桥梁名称
   * @returns {string} 桥梁名称，如果不存在则返回"未知桥梁"
   */
  getCurrentBridgeName() {
    try {
      const bridgeData = localStorage.getItem('selectedBridge');
      if (bridgeData) {
        const bridge = JSON.parse(bridgeData);
        return bridge.name || "未知桥梁";
      }
    } catch (error) {
      console.error('获取当前桥梁名称失败:', error);
    }
    return "未知桥梁";
  },
  
  /**
   * 直接通过指定的桥梁ID获取视频监控URL
   * @param {number} bridgeId 桥梁ID
   * @param {string} [bridgeName] 桥梁名称，用于错误提示
   * @returns {Promise<{url: string, isValid: boolean}>} 视频监控URL及其有效性
   */
  async getVideoUrlByBridgeId(bridgeId, bridgeName = null) {
    if (!bridgeId) {
      console.warn('未提供桥梁ID，使用默认视频监控URL');
      return { url: this.DEFAULT_URL, isValid: false };
    }
    
    // 如果未提供桥梁名称，尝试获取
    if (!bridgeName) {
      bridgeName = this.getCurrentBridgeName();
    }
    
    
    // 从API获取
    try {
      console.log(`正在获取桥梁${bridgeId}的摄像头设备列表...`);
      const response = await getDevicesByBridge(bridgeId);
      console.log(`收到设备API响应:`, response);
      
      // 修复响应码判断 - API返回200而不是0
      if (response && response.code === 200 && response.data && response.data.length > 0) {
        // 详细输出设备列表，方便调试
        console.log('所有设备:', response.data);
        
        // 过滤出类型为CAMERA的设备
        const cameraDevices = response.data.filter(device => device.device_type === 'CAMERA' && device.enabled === 1);
        console.log('过滤后的摄像头设备:', cameraDevices);
        
        if (cameraDevices.length > 0) {
          console.log(`找到${cameraDevices.length}个摄像头设备:`, cameraDevices.map(d => d.name).join(', '));
          // 获取第一个摄像头设备的URL
          const cameraDevice = cameraDevices[0];
          
          // 输出详细设备信息，方便调试
          console.log('选择的摄像头设备详细信息:', cameraDevice);
          
          // 构造URL - 如果设备数据中有完整的URL则使用，否则构造一个基础URL
          let videoUrl = cameraDevice.host;
          
          // 验证URL是否为空
          if (!videoUrl) {
            console.warn('摄像头设备host字段为空，使用默认URL');
            return { url: this.DEFAULT_URL, isValid: false };
          }
          
          // 如果不是以http开头的URL，则补全协议
          if (videoUrl && !videoUrl.startsWith('http')) {
            videoUrl = `http://${videoUrl}`;
          }
          
          // 如果有端口号，则添加端口号
          if (cameraDevice.port) {
            // 检查URL中是否已包含端口号
            if (!videoUrl.includes(`:${cameraDevice.port}`)) {
              videoUrl = `${videoUrl}:${cameraDevice.port}`;
            }
          }
          
          // // 如果设备信息中没有完整的URL参数，则使用默认参数
          // if (!videoUrl.includes('?')) {
          //   videoUrl += '/dispatch.asp?user=admin&pass=Few181818&page=preview.asp[&slice=8&open={1,2,3,4,5,6,7,8}]';
          // }
          
          console.log('最终生成的视频监控URL:', videoUrl);
          
          return { url: videoUrl, isValid: true };
        } else {
          console.warn(`未找到可用的摄像头设备，未能检索到类型为CAMERA且启用状态为1的设备`);
          return { url: this.DEFAULT_URL, isValid: false };
        }
      } else {
        console.warn(`API响应异常或无数据: code=${response?.code}, dataLength=${response?.data?.length || 0}`);
        return { url: this.DEFAULT_URL, isValid: false };
      }
    } catch (error) {
      console.error('获取摄像头设备列表失败:', error);
      return { url: this.DEFAULT_URL, isValid: false };
    }
  },

  /**
   * 获取桥梁的摄像头设备列表（新增方法）
   * @param {number} bridgeId 桥梁ID
   * @param {string} [bridgeName] 桥梁名称，用于错误提示
   * @returns {Promise<{cameras: Array, isValid: boolean, bridgeName: string}>} 摄像头设备列表及其有效性
   */
  async getCameraDevicesByBridgeId(bridgeId, bridgeName = null) {
    if (!bridgeId) {
      console.warn('未提供桥梁ID');
      return { cameras: [], isValid: false, bridgeName: bridgeName || '未知桥梁' };
    }
    
    // 如果未提供桥梁名称，尝试获取
    if (!bridgeName) {
      bridgeName = this.getCurrentBridgeName();
    }
    
    // 从API获取
    try {
      console.log(`正在获取桥梁${bridgeId}的摄像头设备列表...`);
      const response = await getDevicesByBridge(bridgeId);
      console.log(`收到设备API响应:`, response);
      
      // 修复响应码判断 - API返回200而不是0
      if (response && response.code === 200 && response.data && response.data.length > 0) {
        // 详细输出设备列表，方便调试
        console.log('所有设备:', response.data);
        
        // 过滤出类型为CAMERA的设备
        const cameraDevices = response.data.filter(device => device.device_type === 'CAMERA' && device.enabled === 1);
        console.log('过滤后的摄像头设备:', cameraDevices);
        
        if (cameraDevices.length > 0) {
          console.log(`找到${cameraDevices.length}个摄像头设备:`, cameraDevices.map(d => d.name).join(', '));
          
          return { 
            cameras: cameraDevices, 
            isValid: true, 
            bridgeName: bridgeName 
          };
        } else {
          console.warn(`未找到可用的摄像头设备，未能检索到类型为CAMERA且启用状态为1的设备`);
          return { 
            cameras: [], 
            isValid: false, 
            bridgeName: bridgeName 
          };
        }
      } else {
        console.warn(`API响应异常或无数据: code=${response?.code}, dataLength=${response?.data?.length || 0}`);
        return { 
          cameras: [], 
          isValid: false, 
          bridgeName: bridgeName 
        };
      }
    } catch (error) {
      console.error('获取摄像头设备列表失败:', error);
      return { 
        cameras: [], 
        isValid: false, 
        bridgeName: bridgeName 
      };
    }
  },

  /**
   * 根据摄像头设备构造视频监控URL
   * @param {Object} cameraDevice 摄像头设备对象
   * @returns {string} 构造的视频监控URL
   */
  buildVideoUrlFromCamera(cameraDevice) {
    if (!cameraDevice || !cameraDevice.host) {
      console.warn('摄像头设备信息无效或host字段为空');
      return this.DEFAULT_URL;
    }

    // 构造URL - 如果设备数据中有完整的URL则使用，否则构造一个基础URL
    let videoUrl = cameraDevice.host;
    
    // 如果不是以http开头的URL，则补全协议
    if (videoUrl && !videoUrl.startsWith('http')) {
      videoUrl = `http://${videoUrl}`;
    }
    
    // 如果有端口号，则添加端口号
    if (cameraDevice.port) {
      // 检查URL中是否已包含端口号
      if (!videoUrl.includes(`:${cameraDevice.port}`)) {
        videoUrl = `${videoUrl}:${cameraDevice.port}`;
      }
    }
    
    console.log('构造的视频监控URL:', videoUrl);
    return videoUrl;
  },
  
  /**
   * 获取视频监控URL
   * 直接从API获取当前选中桥梁的视频监控URL
   * @returns {Promise<{url: string, isValid: boolean, bridgeName: string}>} 视频监控URL及其有效性
   */
  async getVideoUrl() {
    // 获取当前选中的桥梁ID
    const bridgeId = this.getCurrentBridgeId();
    const bridgeName = this.getCurrentBridgeName();
    const result = await this.getVideoUrlByBridgeId(bridgeId, bridgeName);
    return { ...result, bridgeName };
  }
};

// 初始化并导出
export default VideoUrlManager.init(); 